import PortalIdParser from 'PortalIdParser';
import { getFullUrl, getFullUrlWithCurrentSubDomain } from 'hubspot-url-utils';
import { devtoolsFlags } from 'collaboration-sidebar-common/features/devtools/flags';
import { getWindowSearch } from './params';
import { TABS, VALUES_OF_TABS } from '../constants/tabs';
import { hasCommentingFeature, hasFeature } from './features';
import { COLLABORATION_SIDEBAR_QUERY_PARAMETERS } from '../constants';
import { COLLABORATION_SIDEBAR_QUERY_PARAMETERS_AS_KEYS } from '../constants/url';
import { Metrics } from './metrics';
import { getHubSpotWindowInfo } from 'collaboration-sidebar-common/utils/hubspotWindow';
const getBaseUrl = currentHostname => {
  if (devtoolsFlags.useLocalSidebar) {
    return getFullUrlWithCurrentSubDomain(currentHostname);
  }
  // When the parent page is on a local/worktree hostname (e.g.
  // abu-dhabi-v1.local.hubspotqa.com), the iframe src must use the same origin.
  // Without this, getFullUrl('app') produces app.hubspotqa.com — a different
  // origin — which breaks ui-addon-iframeable's postMessage handshake and the
  // leader/follower transport (causing an error toast after the init timeout).
  try {
    const localHostname = new URL(getFullUrl('local')).hostname;
    const appHostname = new URL(getFullUrl('app')).hostname;
    if (currentHostname === localHostname || currentHostname.endsWith(`.${localHostname}`) || currentHostname.endsWith(`.preview.${appHostname}`)) {
      return getFullUrlWithCurrentSubDomain(currentHostname);
    }
  } catch (_unused) {
    // ignore
  }
  return getFullUrl('app');
};
const EMBEDDABLE_APP_PATH = 'collaboration-sidebar-embeddable';
const MINI_COMMENTER_EMBEDDABLE_APP_PATH = 'collaboration-sidebar-mini-commenter';
export const COMMENTING_QUERYSTRING_PARAM = `${COLLABORATION_SIDEBAR_QUERY_PARAMETERS.commenting}=true`;
export function getEmbeddablePanelUrl({
  objectType,
  objectId,
  ctaUrl,
  appName,
  currentLocation = window.location,
  tab = TABS.COMMENTS,
  enabledFeatures
}) {
  var _windowInfo$sessionId;
  const windowInfo = getHubSpotWindowInfo(window);
  const hasCommentingEnabled = hasCommentingFeature(enabledFeatures);

  // If commenting is disabled, we don't want to pass the objectId and objectType to the embeddable app
  // because it will cause the early requester to try and fetch comments.
  const searchParams = new URLSearchParams({
    objectType: hasCommentingEnabled && objectType != null ? objectType : '',
    objectId: hasCommentingEnabled && objectId != null ? String(objectId) : '',
    tab,
    ctaUrl: ctaUrl !== null && ctaUrl !== void 0 ? ctaUrl : '',
    appName: appName !== null && appName !== void 0 ? appName : '',
    sessionId: (_windowInfo$sessionId = windowInfo === null || windowInfo === void 0 ? void 0 : windowInfo.sessionId) !== null && _windowInfo$sessionId !== void 0 ? _windowInfo$sessionId : ''
  });
  const baseUrl = getBaseUrl(currentLocation.hostname);
  return `${baseUrl}/${EMBEDDABLE_APP_PATH}/${PortalIdParser.get()}?${searchParams}`;
}
export function getEmbeddableMiniCommenterUrl({
  objectType,
  objectId,
  moduleId,
  ctaUrl,
  appName,
  currentLocation = window.location,
  autoFocus,
  parentCommentId
}) {
  const windowInfo = getHubSpotWindowInfo(window);
  const params = [`objectType=${objectType}`, `objectId=${encodeURIComponent(objectId || '')}`, `moduleId=${moduleId}`, `ctaUrl=${encodeURIComponent(ctaUrl || '')}`, `appName=${encodeURIComponent(appName || '')}`, `sessionId=${windowInfo === null || windowInfo === void 0 ? void 0 : windowInfo.sessionId}`];
  if (parentCommentId) {
    params.push(`parentCommentId=${parentCommentId}`);
  }
  if (autoFocus) {
    params.push('autoFocus=true');
  }
  const baseUrl = getBaseUrl(currentLocation.hostname);
  return `${baseUrl}/${MINI_COMMENTER_EMBEDDABLE_APP_PATH}/${PortalIdParser.get()}?${params.join('&')}`;
}

// Returns an object with only the query parameters that are part of the collaboration sidebar
export function parseQueryParams() {
  const search = getWindowSearch();
  const searchParams = new URLSearchParams(search);
  return COLLABORATION_SIDEBAR_QUERY_PARAMETERS_AS_KEYS.reduce((acc, sideBarQueryParam) => {
    const value = searchParams.get(sideBarQueryParam);
    if (value) {
      acc[sideBarQueryParam] = value;
    }
    return acc;
  }, {});
}
export function getOpenQueryParam() {
  return parseQueryParams()[COLLABORATION_SIDEBAR_QUERY_PARAMETERS.csOpen] === 'true';
}
export function getTabQueryParam() {
  return parseQueryParams()[COLLABORATION_SIDEBAR_QUERY_PARAMETERS.csTab] || TABS.COMMENTS;
}
export function getSecuredTabQueryParam({
  enabledFeatures
}) {
  const requestedTab = getTabQueryParam();
  const existingRequestedTab = VALUES_OF_TABS.find(tab => tab === requestedTab);
  if (existingRequestedTab && hasFeature(enabledFeatures, existingRequestedTab)) {
    return existingRequestedTab;
  }

  // If not return the first enabled tab
  return VALUES_OF_TABS.find(tab => hasFeature(enabledFeatures, tab));
}
export function getCommentingThreadQueryParam() {
  return parseQueryParams().commentThread || null;
}
export function getSelectedObjectIdQueryParam() {
  return decodeURIComponent(parseQueryParams()[COLLABORATION_SIDEBAR_QUERY_PARAMETERS.csSelectedObjectId] || '');
}
export function getSelectedObjectTypeQueryParam() {
  return parseQueryParams()[COLLABORATION_SIDEBAR_QUERY_PARAMETERS.csSelectedObjectType];
}
export function getNotificationSettingsOpenQueryParam() {
  return parseQueryParams()[COLLABORATION_SIDEBAR_QUERY_PARAMETERS.notificationSettingsOpen] === 'true';
}
export function getIsCommentingUrl() {
  return window.location.search.includes(COMMENTING_QUERYSTRING_PARAM);
}
export function getCommentIdParam() {
  return parseQueryParams()[COLLABORATION_SIDEBAR_QUERY_PARAMETERS.commentId];
}
export function getParentCommentIdQueryParam() {
  return parseQueryParams()[COLLABORATION_SIDEBAR_QUERY_PARAMETERS.parentCommentId] || null;
}
export function getCommentThreadParam() {
  const parentCommentId = getParentCommentIdQueryParam();
  if (parentCommentId !== null) {
    Metrics.counter('comment-thread-deeplink-param', {
      type: 'parentCommentId'
    }).increment();
    return parentCommentId;
  }
  const commentThread = getCommentingThreadQueryParam();
  if (commentThread !== null) {
    Metrics.counter('comment-thread-deeplink-param', {
      type: 'commentThread'
    }).increment();
  }
  return commentThread;
}